import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { parseOffers } from 'state/global-state/models/loyalty/offers/offers.utils';
import { IAppliedRewards, ICartEntryAdapter } from 'state/loyalty/hooks/types';
import {
  normalizeCartEntriesForOfferEvaluation,
  parseEntry,
} from 'state/loyalty/hooks/utils/incentive-evaluation';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';

import { EvaluateLoyaltyServiceOffersParams } from '../hooks/use-evaluate-loyalty-service-offers';

interface BuildAppliedOfferServiceInputsParams {
  /** Offers persisted as applied to the cart (Redux `appliedOffers`). */
  appliedOffers: LoyaltyAppliedOffer[];
  /** Legacy cart entries to validate the applied offers against. */
  cartEntries?: ILegacyCartEntry[];
  /**
   * Applied rewards, used to drop cart lines fully covered by a reward so they
   * aren't double-counted against offer requirements.
   */
  appliedRewards?: IAppliedRewards | null;
}

/**
 * Shapes persisted applied offers + legacy cart entries into the
 * `appliedIncentives` / `cartEntries` inputs `evaluateLoyaltyServiceOffers`
 * expects.
 *
 * This is the ON-gate legacy-cart shaping shared by the `your-cart` validator
 * (`useLoyaltyCartEntriesEvaluation`) and the menu-screen backfill
 * (`useCartAvailabilityCheck`), which previously duplicated it line-for-line.
 * The minimal-cart seam (`useCartIncentivesEvaluation`) intentionally shapes its
 * inputs differently (from cart-entry lines, not Redux applied offers) and does
 * NOT use this builder.
 *
 * Applied offers are passed explicitly rather than read from Redux so both call
 * sites stay honest about their source (selector vs. hook param).
 */
export const buildAppliedOfferServiceInputs = ({
  appliedOffers,
  cartEntries,
  appliedRewards,
}: BuildAppliedOfferServiceInputsParams): Pick<
  EvaluateLoyaltyServiceOffersParams,
  'appliedIncentives' | 'cartEntries'
> => {
  const appliedIncentives = parseOffers(appliedOffers);
  const parsedEntries = (
    normalizeCartEntriesForOfferEvaluation(cartEntries, appliedOffers) ?? []
  ).reduce(
    parseEntry({
      type: LoyaltyPromotionType.OFFER,
      incentivesToExclude: appliedRewards,
    }),
    [] as ICartEntryAdapter[]
  );

  return { appliedIncentives, cartEntries: parsedEntries };
};
